<template>
  <b-container class="mt-2" style="width:400px">
    <b-card v-if="enrollment" header="TWA Enrollment" class="text-center">
      <b-card-text class="text-left">
        <!-- Course info - shown across all views -->
        <div class="mb-4 pb-3 border-bottom">
          <div><strong>Course:</strong> {{ enrollment.lti_course.context_title }}</div>
          <div v-if="enrollment.lti_course.teachers && enrollment.lti_course.teachers.length">
            <strong>{{ enrollment.lti_course.teachers.length === 1 ? 'Teacher:' : 'Teachers:' }}</strong>
            <span v-for="(teacher, index) in enrollment.lti_course.teachers" :key="index">
              {{ teacher.display_name }}<span v-if="index < enrollment.lti_course.teachers.length - 1">, </span>
            </span>
          </div>
          <div>
            <strong>Unit(s):</strong>
            <span v-for="(product, index) in enrollment.lti_course.products" :key="index">
              {{ product.name }}<span v-if="index < enrollment.lti_course.products.length - 1">, </span>
            </span>
          </div>
        </div>

        <div v-if="show_form === 'show_agreement'">
          <template v-if="hasEnrollAction">
            <b>Review Terms</b>
            <div class="mt-2 mb-4">
              <b-form-checkbox v-model="form.agreed" name="agreed" value="yes" unchecked-value="no">
                Yes, I have read and agree with TWA Textbook's
                <a href="https://truewayasl.com/terms" target="_blank" rel="noopener noreferrer">Terms of Use</a>
                and <a href="https://truewayasl.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
              </b-form-checkbox>
            </div>
          </template>
          <p v-if="auto_enroll_available">
            Review the terms and click Enroll to complete your enrollment.
          </p>
          <p v-else-if="isBlocked" class="text-danger">
            {{ blockMessage }}
          </p>
          <p v-else-if="card_available && accepts_access_code">
            Please choose below to enter your access code or to make payment to complete your enrollment.
          </p>
          <p v-else-if="card_available">
            Please click below to make payment to complete your enrollment.
          </p>
          <p v-else-if="accepts_access_code">
            Please click below to enter your access code to complete your enrollment.
          </p>
          <div v-if="auto_enroll_available" class="mt-4">
            <b-button variant="primary" block :disabled="form.agreed === 'no'" @click="autoEnroll">Agree &amp; Enroll
              <b-spinner v-if="isSending" class="m-1" label="Spinning" small/>
            </b-button>
          </div>
          <div v-else-if="isBlocked && !cardUnavailable" class="mt-4">
            <b-button variant="outline-primary" block @click="openBeacon">Contact TWA</b-button>
          </div>
          <div v-else-if="!isBlocked" class="mt-4" :class="accepts_access_code && card_available ? 'd-flex' : ''">
            <b-button v-if="accepts_access_code" variant="primary" :block="!card_available" :class="{'flex-fill mr-2': card_available}" :disabled="form.agreed === 'no'"
                      @click="show_form = 'coupons'">Enter Access Code
            </b-button>
            <b-button v-if="card_available" variant="primary" :block="!accepts_access_code" class="flex-fill"
                      :disabled="form.agreed === 'no'" @click="checkout">Make Payment
            </b-button>
          </div>
          <!-- TWA-201: shown whether or not the access-code path is still open -->
          <template v-if="cardUnavailable">
            <p v-if="!isBlocked" class="mt-4 mb-2 text-danger">{{ blockMessage }}</p>
            <div class="d-flex" :class="{'mt-4': isBlocked}">
              <b-button :variant="isBlocked ? 'primary' : 'outline-primary'" class="flex-fill mr-2" :disabled="isRetrying" @click="retryLoad">Try Again
                <b-spinner v-if="isRetrying" class="m-1" label="Retrying" small/>
              </b-button>
              <b-button variant="outline-primary" class="flex-fill" @click="openBeacon">Contact TWA</b-button>
            </div>
          </template>
        </div>

        <div v-else-if="show_form === 'coupons'">
          <b>Enter Access Code</b>
          <p class="mt-2">Enter your access code to complete your enrollment.</p>
          <!-- TWA-589: the second sentence names a button, so only when that button exists -->
          <p>
            Access codes are usually sold by the bookstore or given by your school.
            <template v-if="card_available">If you don't have one, you can pay by card by choosing the "Make Payment" button.</template>
          </p>
          <b-input-group prepend="Access Code" class="mt-2">
            <b-form-input maxlength="20" v-model.trim="form.coupon" type="text" @keyup.enter="verifyCoupon"></b-form-input>
          </b-input-group>
          <p v-if="coupon_error" class="mt-3 text-danger">{{ coupon_error }}</p>
          <div class="mt-3" :class="card_available || requiresSupport ? 'd-flex' : ''">
            <b-button v-if="card_available" variant="outline-primary" class="flex-fill mr-2" :disabled="isSending" @click="backToAgreement">Back</b-button>
            <b-button variant="primary" :block="!card_available && !requiresSupport" class="flex-fill" :class="{'mr-2': requiresSupport}" @click="verifyCoupon">Submit
              <b-spinner v-if="isSending" class="m-1" label="Spinning" small/>
            </b-button>
            <b-button v-if="requiresSupport" variant="outline-primary" class="flex-fill" @click="openBeacon">
              Contact TWA
            </b-button>
          </div>
        </div>

        <div v-else-if="show_form === 'collect_user_info'">
          <b>Create Your Account</b>
          <p class="mt-2">Please provide your name and create a password to complete enrollment.</p>
          <b-input-group class="mt-2">
            <template #prepend>
              <b-input-group-text style="width: 105px;">First Name</b-input-group-text>
            </template>
            <b-form-input name="first_name" v-model="form.first_name" type="text"></b-form-input>
          </b-input-group>
          <b-input-group class="mt-2">
            <template #prepend>
              <b-input-group-text style="width: 105px;">Last Name</b-input-group-text>
            </template>
            <b-form-input name="last_name" v-model="form.last_name" type="text"></b-form-input>
          </b-input-group>
          <b-input-group class="mt-2">
            <template #prepend>
              <b-input-group-text style="width: 105px;">Password</b-input-group-text>
            </template>
            <b-form-input name="password" v-model="form.password" type="password"></b-form-input>
          </b-input-group>
          <b-input-group class="mt-2">
            <template #prepend>
              <b-input-group-text style="width: 105px;">Confirm</b-input-group-text>
            </template>
            <b-form-input name="confirm_password" v-model="form.confirm_password" type="password"></b-form-input>
          </b-input-group>
          <ul class="mt-3 list-unstyled">
            <li v-for="error in user_info_errors" :key="error" class="text-danger">{{ error }}</li>
          </ul>
          <div :class="requiresSupport ? 'd-flex mt-3' : 'mt-3'">
            <b-button variant="primary" :block="!requiresSupport" class="flex-fill mr-2" @click="submitUserInfo">Complete Enrollment
              <b-spinner v-if="isSending" class="m-1" label="Spinning" small/>
            </b-button>
            <b-button v-if="requiresSupport" variant="outline-primary" class="flex-fill" @click="openBeacon">
              Contact TWA
            </b-button>
          </div>
        </div>

        <div v-else-if="show_form === 'enrolled' || show_form === 'already_enrolled'">
          <div v-if="enrollment.enrollment_type === 'lti_1.1'">
            <p>Congratulations! You're enrolled in a TWA Textbook course.</p>
            <p>You may close this window and return to your course.</p>
            <p class="mt-3">Enjoy learning ASL!</p>
          </div>
          <div v-else>
            <p>Congratulations! You're enrolled in a TWA Textbook course.</p>
            <p class="mb-0">Enjoy learning ASL!</p>

            <div v-if="canvasCourseUrl" class="mt-3">
              <a :href="canvasCourseUrl" class="btn btn-primary btn-block" target="_blank" rel="noopener noreferrer">
                Go to Course
              </a>
            </div>
          </div>
        </div>

        <div v-else>
          Hang on. Getting info...
          <b-spinner class="ml-2" style="width: 1.5rem; height: 1.5rem;"
                     variant="secondary"></b-spinner>
        </div>

        <stripe-checkout ref="checkoutRef" :pk="stripe_key" :session-id="session_id"/>
      </b-card-text>
    </b-card>
    <div v-if="enrollment" class="text-muted small text-center mt-2">
      token: {{ enrollment.token }}
    </div>
    <b-card v-else-if="errorMessage || isRetrying" header="TWA Enrollment" class="text-center">
      <b-card-text>
        <h5>Hmm...</h5>
        <p class="text-danger">{{ errorMessage }}</p>
        <div :class="canRetry ? 'd-flex mt-3' : 'mt-3'">
          <b-button v-if="canRetry" variant="primary" class="flex-fill mr-2" @click="getEnrollment" :disabled="isRetrying">Try Again
            <b-spinner v-if="isRetrying" class="m-1" label="Retrying" small/>
          </b-button>
          <b-button variant="outline-primary" :block="!canRetry" class="flex-fill" @click="openBeacon">
            Contact TWA
          </b-button>
        </div>
      </b-card-text>
    </b-card>
    <b-card v-else header="TWA Enrollment" class="text-center">
      Hang on. Getting info...
      <b-spinner class="ml-2" style="width: 1.5rem; height: 1.5rem;"
                 variant="secondary"></b-spinner>
    </b-card>
  </b-container>
</template>

<script>
import {verifyCoupon, getEnrollment, processEnrollment, autoEnroll} from "../utils/lti-api"
import {StripeCheckout} from '@vue-stripe/vue-stripe'
import {getErrorMessage} from "../utils/error-messages"
import {ENROLLMENT_ACTIONS} from "../utils/enrollment-actions"
import {initBeacon, setBeaconData, clearBeaconError, openBeacon, identifyBeaconUser, reportErrorToBeacon, reportValidationErrorToBeacon} from "../utils/beacon"
import {MIN_COUPON_LENGTH} from "../utils/constants"

export default {
  components: {
    StripeCheckout,
  },
  data() {
    return {
      form: {
        agreed: 'no',
        coupon: null,
        first_name: null,
        last_name: null,
        password: null,
        confirm_password: null
      },
      isSending: false,
      isRetrying: false,
      show_form: 'default',
      auto_enroll_available: false,
      auto_enroll_state: null,
      auto_enroll_level: null,
      coupon_error: null,
      user_info_errors: [],
      enrollment: null,
      errorMessage: null,
      error_reason: null,
      beaconContext: {},
    }
  },
  computed: {
    token() {
      return this.$route.query.token || null
    },
    accepts_payment() {
      return this.enrollment.lti_course.lti_consumer.accepts_payment
    },
    accepts_access_code() {
      return this.enrollment.lti_course.lti_consumer.accepts_access_code
    },
    // TWA-201: the card button must follow the SESSION, not just the flag. A null
    // session_id is the API's "no checkout available" answer, so accepts_payment can be
    // true with no session to send the student to — rendering the button there is a dead
    // click. Deliberately does not care WHY it is null (price archived, Stripe down, card
    // payment off): the button is only ever as good as the session behind it. The
    // access-code path is untouched; when there is no code path either, hasEnrollAction
    // falls through to the existing Contact TWA dead end.
    card_available() {
      return this.accepts_payment && this.session_id !== null
    },
    // TWA-201: the school takes cards but Stripe handed back no session.
    // ⚠️ The !autoEnrollMode guard is belt-and-braces, NOT load-bearing: whenever a binding
    // is live both controllers already force accepts_payment false in the payload (TWA-242 —
    // api/lti_controller.rb, lti/data_controller.rb), so a bound course should never arrive
    // here with the flag true. Kept because if a stale or hand-built payload ever did, that
    // student needs the closed-course message rather than a retry button that cannot help.
    // Scoped to the agreement screen: a processed enrollment also has a null session_id, and
    // without this every returning student would report 'card_unavailable' to Beacon.
    cardUnavailable() {
      return this.show_form === 'show_agreement' && !this.autoEnrollMode && this.accepts_payment && !this.card_available
    },
    autoEnrollMode() {
      // A bound auto-enroll code puts the consumer in site-license mode: enroll-or-block, no self-pay.
      // auto_enroll_state is a bound state (available/at_cap/inactive/expired/mismatch/multi_product;
      // inactive = a turned-off code, TWA-760, which takes the generic block message below)
      // vs 'none'/null when unbound.
      return this.auto_enroll_state && this.auto_enroll_state !== 'none'
    },
    blockMessage() {
      if (this.auto_enroll_state === 'at_cap') {
        // auto_enroll_level says WHICH pool ran dry (TWA-230). Default to the school
        // wording when absent (older API response) — that was the only wording before.
        return this.auto_enroll_level === 'course'
          ? 'Your course has run out of available spots. Please let your teacher know.'
          : 'Your school has run out of available spots. Please let your teacher know.'
      }
      // TWA-201: no card session is a Stripe problem, not a closed course — retry, don't
      // send the student to their teacher.
      if (this.cardUnavailable) {
        return 'Card payment is temporarily unavailable. Please try again or contact TWA.'
      }
      return "This course isn't available to enroll right now. Please let your teacher know."
    },
    hasEnrollAction() {
      // The agree checkbox only makes sense when there's an actionable button — Agree & Enroll,
      // or the self-pay pay/code buttons. When the student is blocked, there's nothing to agree to.
      return this.auto_enroll_available || (!this.autoEnrollMode && (this.card_available || this.accepts_access_code))
    },
    isBlocked() {
      // On the agreement screen with no available action — a bound code at cap / expired /
      // mismatch, or a consumer with no code and no self-pay. A dead end; we offer support.
      return this.show_form === 'show_agreement' && !this.hasEnrollAction
    },
    stripe_key() {
      return process.env.VUE_APP_STRIPE_KEY
    },
    session_id() {
      return this.enrollment?.session_id ?? null
    },
    canvasCourseUrl() {
      if (!this.enrollment) return null
      const domain = this.enrollment.lti_course.canvas_domain
      const courseId = this.enrollment.lti_course.canvas_course_id
      return `https://${domain}/courses/${courseId}`
    },
    requiresSupport() {
      // Always show "Contact TWA" button on any error
      return this.errorMessage || this.coupon_error || this.user_info_errors.length > 0
    },
    canRetry() {
      // Show retry button only for transient errors
      const retryableErrors = ['network_error', 'canvas_api_error', 'database_unavailable', 'stripe_api_error']
      return retryableErrors.includes(this.error_reason)
    }
  },
  methods: {
    // TWA-589: the agreement checkbox stays ticked; only the code error is dropped
    backToAgreement() {
      this.coupon_error = null
      this.show_form = 'show_agreement'
    },
    async getEnrollment() {
      // Only set isRetrying if there was a previous error (not initial load)
      if (this.errorMessage) {
        this.isRetrying = true
        this.errorMessage = null
      }
      if (this.token) {
        try {
          // Small delay for smoother UX transition
          await new Promise(resolve => setTimeout(resolve, 500))

          const response = await getEnrollment(this.token)
          this.enrollment = response.lti_enrollment
          this.auto_enroll_available = response.auto_enroll_available || false
          this.auto_enroll_state = response.auto_enroll_state || null
          this.auto_enroll_level = response.auto_enroll_level || null

          // Backend determines the action - frontend just follows
          this.show_form = response.action || ENROLLMENT_ACTIONS.SHOW_AGREEMENT

          // Rebuild the full course context here (not just in mounted) so a
          // Try Again retry doesn't leave the pre-load stub as the context
          // every later report re-asserts
          this.buildSessionDataForBeacon()

          // Clear Beacon error data on successful load
          clearBeaconError(this.beaconContext)

          // Blocked, or card-less with only the code path left — attach the reason so support can diagnose.
          if (this.isBlocked || this.cardUnavailable) {
            setBeaconData({
              ...this.beaconContext,
              // TWA-201: auto_enroll_state is the string 'none' when unbound, which is truthy —
              // so the old `|| 'no_self_pay'` fallback reported 'none' and the new reason would
              // never surface. Branch on autoEnrollMode, which already treats 'none' as unbound.
              'Enrollment Blocked': this.autoEnrollMode
                ? this.auto_enroll_state
                // card_unavailable_code_open: card is down but the student can still use a code — not blocked.
                : (this.cardUnavailable
                  ? (this.isBlocked ? 'card_unavailable' : 'card_unavailable_code_open')
                  : 'no_self_pay'),
              'User Message': this.blockMessage
            })
          }
        } catch (error) {
          this.errorMessage = getErrorMessage(error)
          this.error_reason = error.response?.data?.error?.code || 'network_error'
          reportErrorToBeacon(error, 'Page Load Error', this.beaconContext)
        }
      } else {
        this.errorMessage = 'No enrollment token provided'
        this.error_reason = 'no_token'
        reportValidationErrorToBeacon(this.errorMessage, 'Missing Token', this.beaconContext)
      }
      this.isRetrying = false
    },
    // TWA-201: retry from the blocked screen. getEnrollment only raises isRetrying when it is
    // recovering from a failed page load; here the page already rendered, so drive it here. A
    // failed retry sets an errorMessage nothing on this screen renders — toast it and clear it.
    async retryLoad() {
      this.isRetrying = true
      await this.getEnrollment()
      if (this.errorMessage) {
        this.notifyMe(this.errorMessage)
        this.errorMessage = null
      }
    },
    async verifyCoupon() {
      this.isSending = true
      // Freeze the code before the await — the input stays editable while the
      // request is in flight, so form.coupon may be mid-retype by the time an
      // error report reads it.
      const code = this.form.coupon
      try {
        if (this.checkCouponForm()) {
          const response = await verifyCoupon(this.token, code)

          // Clear Beacon error data on success
          clearBeaconError(this.beaconContext)

          // Backend returns action (enrolled or collect_user_info)
          if (response.action) {
            this.show_form = response.action
          }
        } else {
          reportValidationErrorToBeacon(this.coupon_error, 'Access Code Error', this.beaconContext, code || '')
        }
      } catch (error) {
        this.coupon_error = getErrorMessage(error)
        reportErrorToBeacon(error, 'Access Code Error', this.beaconContext, code)
      }
      this.isSending = false
    },
    async autoEnroll() {
      this.isSending = true
      try {
        const response = await autoEnroll(this.token)

        // Clear Beacon error data on success
        clearBeaconError(this.beaconContext)

        if (response.action === ENROLLMENT_ACTIONS.SHOW_AGREEMENT) {
          // Seat taken between load and click (race) - fall back to pay/code
          this.auto_enroll_available = response.auto_enroll_available || false
          this.auto_enroll_state = response.auto_enroll_state || 'at_cap'
          this.auto_enroll_level = response.auto_enroll_level || this.auto_enroll_level
        } else if (response.action) {
          this.show_form = response.action
        }
      } catch (error) {
        // show_agreement has no inline error slot (unlike coupons/collect_user_info) -
        // use the toast, matching checkout()'s error handling on this same screen.
        this.notifyMe(getErrorMessage(error))
        reportErrorToBeacon(error, 'Auto-Enroll Error', this.beaconContext)
      }
      this.isSending = false
    },
    async checkout() {
      try {
        this.$refs.checkoutRef.redirectToCheckout();
      } catch (error) {
        this.notifyMe(getErrorMessage(error))
        reportErrorToBeacon(error, 'Stripe Checkout Error', this.beaconContext)
      }
    },

    checkCouponForm() {
      this.coupon_error = null

      if (!this.form.coupon) {
        this.coupon_error = 'Please enter the access code.'
        return false
      }

      if (this.form.coupon.length < MIN_COUPON_LENGTH) {
        this.coupon_error = 'This access code is not recognized. Please check for typos.'
        return false
      }

      return true
    },

    notifyMe(message, variant = 'danger') {
      this.$bvToast.toast(message, {
        title: 'Notification',
        solid: true,
        variant: variant,
        autoHideDelay: 2500,
        appendToast: true
      })
    },
    async submitUserInfo() {
      if (!this.checkUserInfoForm()) return

      this.isSending = true
      try {
        const name = this.form.first_name + ' ' + this.form.last_name
        const result = await processEnrollment(this.token, name, this.form.password)
        if (result.action === ENROLLMENT_ACTIONS.ENROLLED) {
          this.show_form = ENROLLMENT_ACTIONS.ENROLLED

          // Clear Beacon error data on successful enrollment
          clearBeaconError(this.beaconContext)
        } else {
          this.user_info_errors = ['Failed to complete enrollment. Please try again.']
        }
      } catch (error) {
        this.user_info_errors = [getErrorMessage(error)]
        reportErrorToBeacon(error, 'Enrollment Processing Error', this.beaconContext)
      }
      this.isSending = false
    },
    checkUserInfoForm() {
      this.user_info_errors = []

      if (!this.form.first_name || this.form.first_name.length < 2) {
        this.user_info_errors.push("First name needs to be at least 2 letters")
      }
      if (!this.form.last_name || this.form.last_name.length < 2) {
        this.user_info_errors.push("Last name needs to be at least 2 letters")
      }
      if (!this.form.password || this.form.password.length < 8 || this.form.password.length > 64) {
        this.user_info_errors.push("Password needs to be between 8 to 64 characters in length.")
      }
      if (!this.form.confirm_password || this.form.password !== this.form.confirm_password) {
        this.user_info_errors.push("Passwords must match.")
      }
      if (this.form.password && this.form.password !== this.form.password.trim()) {
        this.user_info_errors.push("Password cannot start or end with spaces.")
      }

      const regex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).*$/
      const isValidPassword = regex.test(this.form.password)

      if (!isValidPassword) {
        this.user_info_errors.push("Password must contain at least one uppercase letter, one lowercase letter, one digit, and one special character.")
      }

      const patternRegex = /^(?!(.*123|.*abc|.*password)).*$/
      const noCommonPatterns = patternRegex.test(this.form.password)

      if (!noCommonPatterns) {
        this.user_info_errors.push("Password must not contain common patterns (123, abc, password).")
      }

      if (this.form.password && this.form.first_name && this.form.password.toLowerCase().includes(this.form.first_name.toLowerCase())) {
        this.user_info_errors.push("Password cannot include your first name.")
      }
      if (this.form.password && this.form.last_name && this.form.password.toLowerCase().includes(this.form.last_name.toLowerCase())) {
        this.user_info_errors.push("Password cannot include your last name.")
      }

      return !this.user_info_errors.length
    },
    buildSessionDataForBeacon() {
      // Guard: only build session data if enrollment loaded successfully
      if (!this.enrollment) return

      // Store context for use in error calls (merge-vs-replace semantics undocumented; every call passes the full set)
      this.beaconContext = {
        'Flow': this.enrollment.enrollment_type === 'lti_1.1' ? 'LTI 1.1' : 'Direct Join',
        'Course': this.enrollment.lti_course.context_title,
        'Domain': this.enrollment.lti_course.canvas_domain,
        'Course Id': this.enrollment.lti_course.canvas_course_id,
        'Token': this.token,
        'Email': this.enrollment.email
      }

      setBeaconData(this.beaconContext)
      identifyBeaconUser(this.enrollment.email)
    },
    openBeacon
  },
  async mounted() {
    initBeacon()
    // Minimal context before the fetch: a page-load failure is exactly when support
    // most needs to know which enrollment was attempted. buildSessionDataForBeacon
    // replaces this with full course context on success.
    if (this.token) {
      this.beaconContext = { 'Flow': 'Direct Join / LTI 1.1', 'Token': this.token }
      setBeaconData(this.beaconContext)
    }
    await this.getEnrollment()
  }
};
</script>

<style scoped>
/* Remove underline from button links */
.btn {
  text-decoration: none !important;
}
</style>

